import Control from '@/js/Controls/Control'
import { tns } from 'tiny-slider/src/tiny-slider'
import { TinySliderInstance, TinySliderSettings } from 'tiny-slider'
import React from 'jsx-dom'
import { deepMerge } from 'collapsable.js'

interface HTMLCarouselElement extends HTMLElement {
	carousel: TinySliderInstance
}

declare global {
	// eslint-disable-next-line @typescript-eslint/no-namespace
	namespace JSX {
		interface IntrinsicElements {
			[elemName: string]: any
		}
	}
}

class CarouselControl implements Control {
	public initialize(context: Element | Document): void {
		const carousels = context.querySelectorAll<HTMLCarouselElement>('.js-carousel')

		carousels.forEach((carouselWrapper) => {
			const container = carouselWrapper.querySelector<HTMLElement>('.js-carousel__carousel')
			if (!container) return

			const itemsCount = container.querySelectorAll('.js-carousel__item').length
			if (itemsCount <= 1) return

			const dataOptions = JSON.parse(carouselWrapper.dataset.carousel ?? '{}')
			const controlsContainer = this.buildControlsContainer(carouselWrapper)
			const navContainer = this.buildNavContainer(carouselWrapper, itemsCount)
			const autoplayButton = '.js-carousel__pause'

			const defaultOptions: TinySliderSettings = {
				container,
				controlsContainer,
				navContainer,
				autoplayButton,
				autoplayText: [
					'<span class="icon-[mdi--play]"></span>',
					'<span class="icon-[mdi--pause]"></span>'
				],
				loop: true
			}

			const options = deepMerge({}, defaultOptions, dataOptions)

			carouselWrapper.carousel = tns(options)

			this.restoreNavLabels(navContainer)

			// Set initial tabindex values
			this.updateTabIndexes(carouselWrapper.carousel.getInfo().index, container)

			carouselWrapper.carousel.events.on('indexChanged', (info) => {
				if (carouselWrapper.classList.contains('js-carousel--names')) {
					this.checkVisibleName(navContainer, navContainer.parentElement, info.index)

					this.restoreNavLabels(navContainer)
				}
				// Update tabindex whenever the slide changes
				this.updateTabIndexes(info.index, container)
			})
		})
	}

	private updateTabIndexes(activeIndex: number, container: HTMLElement): void {
		const slides = Array.from(container.querySelectorAll('.js-carousel__item'))

		slides.forEach((slide, index) => {
			const isCurrent = slide.classList.contains('tns-slide-active')
			const focusableElements = slide.querySelectorAll('a, button, input, select, textarea, [tabindex="0"]')

			focusableElements.forEach((el) => {
				if (isCurrent) {
					el.removeAttribute('tabindex')
				} else {
					el.setAttribute('tabindex', '-1')
				}
			})
		})
	}

	private checkVisibleName(nav: HTMLElement, parent: HTMLElement | null, index: number) {
		const wrapWidth = nav.offsetWidth
		const page = nav.children.item(index - 1) as HTMLElement

		if (page && parent) {
			const pageWidth = page.offsetWidth
			const pageLeft = page.offsetLeft

			if (pageLeft + pageWidth < parent.scrollLeft) {
				parent?.scrollTo({
					top: 0,
					left: pageLeft,
					behavior: 'smooth'
				})
			}

			if (pageWidth + pageLeft > wrapWidth + parent.scrollLeft) {
				parent?.scrollTo({
					top: 0,
					left: pageWidth + pageLeft - wrapWidth,
					behavior: 'smooth'
				})
			}
		}
	}

	private restoreNavLabels(navContainer: HTMLElement): void {
		const navButtons = navContainer.querySelectorAll<HTMLButtonElement>('.js-carousel__page')

		navButtons.forEach((btn) => {
			const originalLabel = btn.dataset.originalLabel
			if (originalLabel) {
				btn.setAttribute('aria-label', originalLabel)
			}
		})
	}

	private buildControlsContainer(carouselWrapper: HTMLElement): HTMLElement {
		const controls = carouselWrapper.querySelector<HTMLElement>('.js-carousel__controls')

		if (controls) {
			return controls
		}

		const customControls: Element = (
			<p class="js-carousel__controls">
				<button type="button" class="js-carousel__control js-carousel__control--prev">
					<span class="sr-only">{carouselWrapper.dataset.prev}</span>
				</button>
				<button type="button" class="js-carousel__control js-carousel__control--next">
					<span class="sr-only">{carouselWrapper.dataset.next}</span>
				</button>
			</p>
		)

		carouselWrapper.insertAdjacentElement('afterbegin', customControls)

		return customControls as HTMLElement
	}

	private buildNavContainer(carouselWrapper: HTMLElement, itemsCount: number): HTMLElement {
		const nav = carouselWrapper.querySelector<HTMLElement>('.js-carousel__nav')

		if (nav) {
			return nav
		}

		const customNav: HTMLElement = (<nav class="js-carousel__paging"></nav>) as HTMLElement

		for (let i = 0; i < itemsCount; i++) {
			customNav.appendChild(<button type="button" class="js-carousel__page cursor-pointer" style="display: none"></button>)
		}

		carouselWrapper.insertAdjacentElement('beforeend', customNav)

		return customNav
	}
}

export default new CarouselControl()
